import { useState } from "react";
import { motion } from "framer-motion";
import {
  Check,
  Loader2,
  Sparkles,
  ShieldCheck,
  Info,
  MousePointerClick,
  CreditCard,
  Rocket,
  CalendarCheck,
  KeyRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { createPlanCheckout, createCareCheckout } from "@/lib/api";
import { PRICING } from "@/constants/testIds";

const PLANS = [
  {
    id: "starter",
    name: "Starter",
    monthly: 99,
    total: 1188,
    blurb: "Everything you need to get online and converting.",
    cta: "START YOUR WEBSITE TODAY",
    features: [
      "Up to 3 Custom Pages",
      "Mobile-Responsive Design",
      "Basic SEO & Meta Setup",
      "Contact Form + Email Routing",
      "Social Media Integration",
    ],
  },
  {
    id: "business",
    name: "Business",
    monthly: 149,
    total: 1788,
    popular: true,
    blurb: "Best ROI — built to grow with your business.",
    cta: "START YOUR WEBSITE TODAY",
    features: [
      "Up to 6 Custom Pages",
      "CMS / Blog Integration",
      "Advanced SEO Optimization",
      "Custom Animations & Motion",
      "Speed Optimization (90+ score)",
      "Lead-Capture Funnels",
    ],
  },
  {
    id: "premium",
    name: "Premium",
    monthly: 199,
    total: 2388,
    blurb: "Premium build for serious revenue & e-commerce.",
    cta: "START YOUR WEBSITE TODAY",
    features: [
      "10+ Custom Pages",
      "E-Commerce Integration",
      "Payment Gateway Setup",
      "Premium Motion Design",
      "Priority Support",
      "Analytics & Tracking Setup",
    ],
  },
];

const CARE = [
  {
    id: "care_starter",
    name: "Starter Care",
    monthly: 49,
    blurb: "Keep your site online, secure & updated.",
  },
  {
    id: "care_business",
    name: "Business Care",
    monthly: 79,
    popular: true,
    blurb: "For growing sites that need regular updates.",
  },
  {
    id: "care_premium",
    name: "Premium Care",
    monthly: 99,
    blurb: "Full white-glove care & priority support.",
  },
];

const STEPS = [
  { icon: MousePointerClick, title: "Choose your plan", desc: "Pick the website plan that fits your business." },
  { icon: CreditCard, title: "Pay your first month", desc: "Just your first monthly payment gets things moving today." },
  { icon: Rocket, title: "We design & launch", desc: "Bellso Digital designs and launches your website." },
  { icon: CalendarCheck, title: "12 monthly payments", desc: "Continue the same monthly payment across the 12-month term." },
  { icon: KeyRound, title: "You own it", desc: "After all 12 payments, the completed website code is yours." },
];

function PlanCard({ plan, loadingId, onStart }) {
  return (
    <motion.div
      data-testid={PRICING.card(plan.id)}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className={`relative rounded-3xl p-8 flex flex-col bg-[#111] border ${
        plan.popular
          ? "border-[#CCFF00] glow-neon"
          : "border-[#27272A] hover:border-white/30"
      } transition-all`}
    >
      {plan.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#CCFF00] text-black text-[10px] font-bold uppercase tracking-widest flex items-center gap-1 whitespace-nowrap">
          <Sparkles className="w-3 h-3" /> Most Popular
        </div>
      )}

      <div className="font-mono-jb text-xs uppercase tracking-[0.3em] text-[#CCFF00]">
        {plan.name}
      </div>

      <div className="mt-4 flex items-baseline gap-1">
        <span className="font-display text-6xl md:text-7xl font-black tracking-tighter">
          ${plan.monthly}
        </span>
        <span className="text-zinc-500 text-sm font-medium">/mo</span>
      </div>
      <div className="mt-1 text-sm text-zinc-400 font-mono-jb">
        for 12 months
      </div>

      <div className="mt-4 rounded-xl border border-[#27272A] bg-black/40 p-3 space-y-1">
        <div className="flex items-center justify-between text-sm">
          <span className="text-zinc-400">First payment today</span>
          <span className="font-bold text-[#CCFF00]">${plan.monthly}</span>
        </div>
      </div>

      <p className="mt-4 text-zinc-400 text-sm">{plan.blurb}</p>

      <ul className="mt-6 space-y-3 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-3 text-sm">
            <Check className="w-5 h-5 text-[#CCFF00] shrink-0 mt-0.5" />
            <span className="text-zinc-200">{f}</span>
          </li>
        ))}
      </ul>

      <Button
        data-testid={PRICING.buy(plan.id)}
        onClick={() => onStart(plan)}
        disabled={loadingId === plan.id}
        className={`mt-8 h-12 rounded-none text-sm ${
          plan.popular ? "btn-neon" : "btn-outline-neon"
        }`}
      >
        {loadingId === plan.id ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Redirecting…
          </>
        ) : (
          plan.cta
        )}
      </Button>
    </motion.div>
  );
}

export default function Pricing() {
  const [loadingId, setLoadingId] = useState(null);

  const startPlan = async (plan) => {
    try {
      setLoadingId(plan.id);
      const data = await createPlanCheckout({
        plan_id: plan.id,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout. Try again.");
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Checkout failed. Please try again."
      );
    } finally {
      setLoadingId(null);
    }
  };

  const startCare = async (plan) => {
    try {
      setLoadingId(plan.id);
      const data = await createCareCheckout({
        plan_id: plan.id,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout. Try again.");
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Checkout failed. Please try again."
      );
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <section id="pricing" className="section-pad relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header + promo graphic */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-16">
          <div>
            <span className="font-mono-jb text-xs uppercase tracking-[0.3em] text-[#CCFF00]">
              / Website Ownership Plans
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-bold mt-4 leading-[1.05]">
              Start your website for{" "}
              <span className="text-gradient-neon">$99 today.</span>
            </h2>
            <p className="mt-5 text-zinc-300 text-lg max-w-xl">
              No large upfront website cost. Choose your plan and make{" "}
              <span className="text-white font-semibold">12 monthly payments</span>.
              Design, hosting, maintenance, and support are included during the
              12-month term.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button
                data-testid="pricing-hero-cta"
                onClick={() =>
                  document
                    .getElementById("plan-cards")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className="btn-neon rounded-none px-8 py-6 h-auto text-base"
              >
                Start Your Website Today
              </Button>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="absolute -inset-4 bg-[#CCFF00]/10 blur-3xl rounded-full" />
            <img
              src="/start-99-promo.jpg"
              alt="Start your website for $99 — 12-month plan"
              data-testid="pricing-promo-graphic"
              className="relative w-full max-w-md mx-auto rounded-3xl border border-[#27272A] shadow-2xl"
            />
          </motion.div>
        </div>

        {/* Includes banner */}
        <div
          data-testid="pricing-includes-banner"
          className="rounded-2xl border border-[#CCFF00]/30 bg-[#CCFF00]/5 p-5 mb-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-zinc-200"
        >
          <span className="font-mono-jb text-[11px] uppercase tracking-widest text-[#CCFF00]">
            Every plan includes
          </span>
          {[
            "Hosting",
            "SSL",
            "Maintenance",
            "Technical Support",
            "1 small update / week",
          ].map((x) => (
            <span key={x} className="flex items-center gap-1.5">
              <Check className="w-4 h-4 text-[#CCFF00]" /> {x}
            </span>
          ))}
          <span className="flex items-center gap-1.5 text-zinc-400">
            <Info className="w-4 h-4 text-zinc-500" /> You provide your own domain
          </span>
        </div>

        {/* Plan cards */}
        <div id="plan-cards" className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {PLANS.map((p) => (
            <PlanCard
              key={p.id}
              plan={p}
              loadingId={loadingId}
              onStart={startPlan}
            />
          ))}
        </div>

        {/* 12-Month Agreement disclosure */}
        <div
          data-testid="pricing-agreement-disclosure"
          className="mt-8 rounded-2xl border border-amber-400/30 bg-amber-400/5 p-5 flex gap-3 items-start"
        >
          <ShieldCheck className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <p className="text-sm text-zinc-300 leading-relaxed">
            <span className="font-semibold text-amber-300">
              12-Month Website Agreement:
            </span>{" "}
            These are 12-month website payment plans, not month-to-month plans.
            If the agreement is ended before all 12 payments are completed, the
            remaining website balance becomes due. Your checkout clearly shows
            this is the first payment of a 12-month plan before you pay.
          </p>
        </div>

        {/* How It Works */}
        <div className="mt-24">
          <div className="text-center mb-12">
            <span className="font-mono-jb text-xs uppercase tracking-[0.3em] text-[#CCFF00]">
              / How it works
            </span>
            <h3 className="font-display text-3xl sm:text-4xl tracking-tighter font-bold mt-4">
              Simple from{" "}
              <span className="text-gradient-neon">start to ownership.</span>
            </h3>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="rounded-2xl border border-[#27272A] bg-[#111] p-5"
                  data-testid={`how-step-${i}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-[#CCFF00]/10 border border-[#CCFF00]/30 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#CCFF00]" />
                  </div>
                  <div className="mt-4 font-mono-jb text-[11px] text-zinc-500">
                    Step {i + 1}
                  </div>
                  <div className="font-display text-lg font-bold mt-1">
                    {s.title}
                  </div>
                  <p className="text-sm text-zinc-400 mt-1">{s.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Website Care (after 12 months) */}
        <div className="mt-24">
          <div className="text-center mb-4">
            <span className="font-mono-jb text-xs uppercase tracking-[0.3em] text-[#CCFF00]">
              / After your 12 months
            </span>
            <h3 className="font-display text-3xl sm:text-4xl tracking-tighter font-bold mt-4">
              Optional{" "}
              <span className="text-gradient-neon">Website Care.</span>
            </h3>
            <p className="mt-4 text-zinc-400 max-w-2xl mx-auto">
              Once your website is fully paid, keep it maintained with a
              month-to-month care plan. Optional, and cancel anytime. Major
              redesigns, new pages, new functionality, e-commerce additions, or
              third-party integrations are quoted separately.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mt-8">
            {CARE.map((c) => (
              <motion.div
                key={c.id}
                data-testid={PRICING.card(c.id)}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className={`relative rounded-3xl p-7 flex flex-col bg-[#111] border ${
                  c.popular
                    ? "border-[#CCFF00]/60"
                    : "border-[#27272A] hover:border-white/30"
                } transition-all`}
              >
                {c.popular && (
                  <span className="absolute -top-2.5 right-4 px-2.5 py-0.5 rounded-full bg-[#CCFF00] text-black text-[9px] font-bold uppercase tracking-widest">
                    Popular
                  </span>
                )}
                <div className="font-mono-jb text-xs uppercase tracking-[0.3em] text-[#CCFF00]">
                  {c.name}
                </div>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-display text-4xl font-black tracking-tighter">
                    ${c.monthly}
                  </span>
                  <span className="text-zinc-500 text-sm">/mo</span>
                </div>
                <p className="mt-3 text-sm text-zinc-400 flex-1">{c.blurb}</p>
                <ul className="mt-4 space-y-2 text-sm">
                  {["Hosting", "Routine maintenance", "Technical support", "Small content changes"].map(
                    (f) => (
                      <li key={f} className="flex items-center gap-2 text-zinc-300">
                        <Check className="w-4 h-4 text-[#CCFF00]" /> {f}
                      </li>
                    )
                  )}
                </ul>
                <Button
                  data-testid={PRICING.buy(c.id)}
                  onClick={() => startCare(c)}
                  disabled={loadingId === c.id}
                  className="mt-6 h-11 rounded-none btn-outline-neon text-sm"
                >
                  {loadingId === c.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    `Add ${c.name}`
                  )}
                </Button>
              </motion.div>
            ))}
          </div>
        </div>

        <p className="text-center text-zinc-500 text-sm mt-14 font-mono-jb">
          Secure checkout powered by Stripe · Your first payment starts your
          12-month plan.
        </p>
      </div>
    </section>
  );
}
